import { useState } from "react";
import { H3, Label } from "../Text";
import { toast } from "react-toastify";
import Circle from "../Animation/Circle";
import { Button } from "../Buttons";
import { Request } from "../../hooks/Request";
import TableComplete from "../Tables/TableComplete";
import { requestAuth } from "../services/RequestService";
import { useDialog } from "../../contexts/DialogContext";
import { DialogConfirmation } from "../Modal";
import { numberWithDecimals, validateArray } from "../../utils/validate";
import { FileManagerModal } from "./FileManagerModal";
import { QuoteComparison } from "../common/QuoteComparison";
import Select from "react-select";
import * as XLSX from "xlsx";

const DISCOUNT_ITEM_PATTERNS = [
  /\bDESCUENTOS?\b/,
  /\bDESCUENTO?\b/,
  /\bDCTOS?\b/,
  /\bDTOS?\b/,
  /\bDISCOUNTS?\b/,
  /\bDISCOUNT?\b/,
  /\bREBATES?\b/,
  /\bBONIFICACION(?:ES)?\b/,
];

function normalizeItemText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

function isDiscountItemLabel(value) {
  const normalizedText = normalizeItemText(value);
  return DISCOUNT_ITEM_PATTERNS.some((pattern) => pattern.test(normalizedText));
}

function toQuoteNumber(value) {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  const numericMatch = String(value).replace(/\s/g, "").match(/-?\d[\d.,]*/);
  if (!numericMatch) return 0;

  let normalized = numericMatch[0];
  const hasComma = normalized.includes(",");
  const hasDot = normalized.includes(".");

  if (hasComma && hasDot) {
    normalized =
      normalized.lastIndexOf(",") > normalized.lastIndexOf(".")
        ? normalized.replace(/\./g, "").replace(",", ".")
        : normalized.replace(/,/g, "");
  } else if (hasComma) {
    normalized = /^-?\d{1,3}(,\d{3})+$/.test(normalized)
      ? normalized.replace(/,/g, "")
      : normalized.replace(",", ".");
  } else if (/^-?\d{1,3}(\.\d{3})+$/.test(normalized)) {
    normalized = normalized.replace(/\./g, "");
  }

  const number = Number(normalized);
  return Number.isFinite(number) ? number : 0;
}

function getQuoteItemTotal(itemLabel, cantidad, costoUnitario) {
  const total = toQuoteNumber(cantidad) * toQuoteNumber(costoUnitario);
  const signedTotal = isDiscountItemLabel(itemLabel) ? -Math.abs(total) : total;
  return Number(signedTotal.toFixed(2));
}

const InputSubDataProviderAdd = ({ field, currency }) => {
  const { openDialog, dialogClose } = useDialog();

  const {
    data: quotesData,
    loading: loadingQuotes,
    call: reloadQuotes,
  } = Request({
    urlApi: `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
  });

  const { data: requestItems, loading: loadingRequest } = Request({
    urlApi: `/dataSet/procedure/${field.id}/fieldname/descripciónDelPedido/subdata`,
  });

  const { data: providers, loading: loadingProviders } = Request({
    urlApi: field.urlApi,
  });

  const { data: storedCurrency } = Request({
    urlApi: `/Procedure/${field.id}/dataValues?fieldnames=moneda`,
  });

  const resolvedCurrency = currency || storedCurrency?.moneda;

  const handleApprove = async (selectedItem) => {
    const isCurrentlyApproved = selectedItem.description3 === "true";

    try {
      if (!isCurrentlyApproved) {
        const approvedQuotes = quotesData.filter(
          (q) => q.description3 === "true" && q.id !== selectedItem.id,
        );

        for (const quote of approvedQuotes) {
          await requestAuth("put", `/dataSet/procedure/subData/${quote.id}`, {
            ...quote,
            Label: quote.label,
            Description3: "false",
            dataSetId: field.dataSetId,
          });
        }
      }

      const payload = {
        Label: selectedItem.label,
        Description2: selectedItem.description2,
        NumberValue: selectedItem.numberValue,
        Description3: isCurrentlyApproved ? "false" : "true",
        dataSetId: field.dataSetId,
      };

      await requestAuth(
        "put",
        `/dataSet/procedure/subData/${selectedItem.id}`,
        payload,
      );

      toast.success(
        isCurrentlyApproved
          ? "Aprobación removida"
          : "Proveedor aprobado exclusivamente",
      );
      reloadQuotes();
    } catch (error) {
      console.error(error);
      toast.error("Error al procesar la aprobación");
    }
  };

  function handleForm(item = null) {
    const existingDetails =
      item && item.description2 ? JSON.parse(item.description2) : null;
    openDialog(
      <div className="w-[800px] max-w-full">
        {loadingRequest && requestItems ? (
          <FormAddQuote
            existingQuote={item}
            existingDetails={existingDetails}
            requestItems={requestItems}
            providers={providers}
            field={field}
            reload={reloadQuotes}
            onClose={dialogClose}
            currency={resolvedCurrency}
          />
        ) : (
          <Circle />
        )}
      </div>,
    );
  }

  function handleDelete(data) {
    openDialog(
      <DialogConfirmation
        children={<H3>¿Estás seguro que quieres eliminar esta cotización?</H3>}
        onClose={dialogClose}
        method="delete"
        url={`/dataSet/procedure/subData/${data.id}`}
        texBtn="Eliminar"
        handleSuccess={reloadQuotes}
      />,
    );
  }

  function handleFiles(item) {
    openDialog(
      <FileManagerModal
        item={item}
        reload={reloadQuotes}
        onClose={dialogClose}
      />,
    );
  }

  function handleQuoteComparison(procedureId) {
    openDialog(<QuoteComparison procedureId={procedureId} />);
  }

  return (
    <div className="bg-gray-300/40 p-2 rounded-md">
      <div className="flex justify-between items-center mb-2">
        <div className="">
          <Label htmlFor={field.name} className={`${field.className} my-0`}>
            {field.labelCustom ? field.labelCustom : field.label}
          </Label>
          <Button
            onClick={() => handleQuoteComparison(field.id)}
            className="px-3 py-1 text-sm"
          >
            <i className="fas fa-file-pdf"></i>
          </Button>
        </div>
    <Button
      onClick={() => handleForm(null)}
      className="px-3 py-1 text-sm"
      disabled={!resolvedCurrency}
    >
      + Nueva Cotización
    </Button>
      </div>

      {loadingQuotes ? (
        <div className="w-full">
          <TableComplete
            header={[
              {
                name: "label",
                label: "Proveedor",
                type: "custom",
                builder: (value, _, item) => (
                  <div className="flex items-center gap-2">
                    <span
                      className={
                        item.description3 === "true"
                          ? "font-bold text-green-700"
                          : ""
                      }
                    >
                      {value}
                    </span>
                    {item.description3 === "true" && (
                      <i
                        className="fas fa-certificate text-green-500 text-xs"
                        title="Elegido"
                      ></i>
                    )}
                  </div>
                ),
              },
              {
                name: "total",
                label: "Total Cotizado",
                type: "custom",
                builder: (_, __, item) => {
                  try {
                    const json = JSON.parse(item.description2);
                    const totalItems =
                      json.items?.reduce(
                        (acc, curr) => acc + (Number(curr.costoTotal) || 0),
                        0,
                      ) || 0;
                    const totalLogistics =
                      json.logisticCosts?.reduce(
                        (acc, curr) => acc + (Number(curr.monto) || 0),
                        0,
                      ) || 0;
                    const total = totalItems + totalLogistics;
                    return (
                      <span className="font-mono font-semibold text-gray-700">
                        {numberWithDecimals(Number(total || "0.00"))}
                      </span>
                    );
                  } catch (e) {
                    return (
                      <span className="text-red-400 text-xs">Error data</span>
                    );
                  }
                },
              },
              ...(field.fieldProcessStepId !== 1947
                ? [
                    {
                      name: "description3",
                      label: "Seleccionado",
                      type: "custom",
                      builder: (_, __, item) => {
                        const isApproved = item.description3 === "true";
                        return (
                          <button
                            type="button"
                            onClick={() => handleApprove(item)}
                            className={`flex items-center gap-2 px-3 py-1 rounded-full transition-all duration-300 border ${
                              isApproved
                                ? "bg-green-500 border-green-600 text-white shadow-sm"
                                : "bg-white border-gray-300 text-gray-400 hover:border-green-400 hover:text-green-500"
                            }`}
                          >
                            <i
                              className={`fas ${
                                isApproved ? "fa-check-circle" : "fa-circle"
                              } text-sm`}
                            ></i>
                            <span className="text-[10px] uppercase font-bold">
                              {isApproved ? "Seleccionado" : "Elegir"}
                            </span>
                          </button>
                        );
                      },
                    },
                  ]
                : []),

              {
                name: "add",
                type: "action",
                label: "Acciones",
                sticky: true,
                actions: [
                  {
                    label: "Editar",
                    icon: "fas fa-edit",
                    action: handleForm,
                    color: "text-blue-600",
                  },
                  {
                    label: "Eliminar",
                    icon: "fas fa-trash",
                    action: handleDelete,
                    color: "text-red-400",
                  },
                  {
                    label: "Documentos",
                    icon: "fas fa-file-pdf",
                    action: handleFiles,
                    color: "text-purple-600",
                  },
                ],
              },
            ]}
            data={quotesData}
          />
        </div>
      ) : (
        <Circle />
      )}
    </div>
  );
};

// Componente FormAddQuote se mantiene igual pero asegurando que Description3 viaje en el payload
const FormAddQuote = ({
  existingQuote,
  existingDetails,
  requestItems,
  providers,
  field,
  reload,
  onClose,
  currency,
}) => {
  const [providerName, setProviderName] = useState(existingQuote?.label || "");
  const [paymentTerms, setPaymentTerms] = useState(
    existingDetails?.paymentTerms || "",
  );
  const [incoterms, setIncoterms] = useState(existingDetails?.incoterms || "");
  const [deliveryDate, setDeliveryDate] = useState(
    existingDetails?.deliveryDate || "",
  );
  const [logisticCosts, setLogisticCosts] = useState(
    existingDetails?.logisticCosts || [],
  );
  const { data: logisticCostsList } = Request({
    urlApi: "/Dropdown/costos-logisticos/options",
  });
  const { data: paymentTermsList, loading: loadingPaymentTerms } = Request({
    urlApi: "/Dropdown/terminos_pago_dropdown/options",
  });
  const { data: incotermsList, loading: loadingIncoterms } = Request({
    urlApi: "/Dropdown/incoterms_dropdown/options",
  });

  const mapDropdownOptions = (list) =>
    validateArray(list)
      ? list
          .map((option) => ({
            value: option.name || option.label || option.id || "",
            label: option.name || option.label || option.id || "",
          }))
          .filter((option) => option.value)
      : [];

  const handleAddLogisticCost = () => {
    setLogisticCosts([...logisticCosts, { type: "", monto: 0 }]);
  };

  const handleLogisticCostChange = (index, fieldName, value) => {
    const newCosts = [...logisticCosts];
    newCosts[index][fieldName] = value;
    setLogisticCosts(newCosts);
  };

  const activeCurrency = currency || "USD";
  const currencySymbol =
    activeCurrency === "USD" ? "$" : activeCurrency === "EUR" ? "€" : activeCurrency === "Bs." ? "Bs." : activeCurrency;
  const formatCost = (value) =>
    `${currencySymbol} ${numberWithDecimals(Number(value || 0))}`;
  const cleanMoneyValue = (value) =>
    String(value || "")
      .replace(/,/g, "")
      .replace(/[^\d.]/g, "")
      .replace(/(\..*)\./g, "$1");
  const formatMoneyInput = (value) => {
    const cleanValue = cleanMoneyValue(value);
    if (!cleanValue) return "";

    const [integerPart, decimalPart] = cleanValue.split(".");
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return cleanValue.includes(".")
      ? `${formattedInteger}.${decimalPart || ""}`
      : formattedInteger;
  };

  const handleRemoveLogisticCost = (index) => {
    const newCosts = [...logisticCosts];
    newCosts.splice(index, 1);
    setLogisticCosts(newCosts);
  };

  const [inputs, setInputs] = useState(() => {
    const initial = {};
    if (existingDetails?.items) {
      existingDetails.items.forEach((det) => {
        const requestItem = validateArray(requestItems)
          ? requestItems.find(
              (item) => String(item.id) === String(det.refSubDataIdPedido),
            )
          : null;
        const itemLabel = requestItem?.label || det.labelItem || "";

        initial[det.refSubDataIdPedido] = {
          ...det,
          costoTotal: getQuoteItemTotal(
            itemLabel,
            det.cantidad,
            det.costoUnitario,
          ),
          isDiscount: isDiscountItemLabel(itemLabel),
        };
      });
    }
    return initial;
  });

  const handleExportItems = () => {
    try {
      if (!validateArray(requestItems)) {
        return toast.warning("No hay items para exportar");
      }

      const exportData = requestItems.map((reqItem) => {
        const row = inputs[reqItem.id] || {};
        // const meta = reqItem.description2 ? JSON.parse(reqItem.description2) : {};
        return {
          ID: reqItem.id,
          Item: reqItem.label,
          // Descripcion: meta.desc || "",
          Cantidad: row.cantidad || "",
          Costo_Unitario: row.costoUnitario || "",
          // Observaciones: row.observaciones || "",
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Items Cotizacion");
      
      // Ajustar anchos de columna
      const wscols = [
        { wch: 10 }, // ID
        { wch: 30 }, // Item
        // { wch: 40 }, // Descripcion
        { wch: 10 }, // Cantidad
        { wch: 15 }, // Costo_Unitario
        // { wch: 30 }, // Observaciones
      ];
      worksheet["!cols"] = wscols;

      XLSX.writeFile(workbook, `Cotizacion_${providerName || "items"}.xlsx`);
      toast.success("Items exportados correctamente");
    } catch (error) {
      console.error(error);
      toast.error("Error al exportar items");
    }
  };

  const handleImportItems = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (!jsonData.length) {
          return toast.warning("El archivo está vacío");
        }

        const newInputs = { ...inputs };
        jsonData.forEach((row) => {
          const id = row.ID || row.id;
          if (id) {
            const requestItem = validateArray(requestItems)
              ? requestItems.find((item) => String(item.id) === String(id))
              : null;
            const itemLabel = requestItem?.label || row.Item || row.item || "";
            const cantidad = row.Cantidad ?? row.cantidad ?? 0;
            const costoUnitario =
              row.Costo_Unitario ?? row.costo_unitario ?? row.costounitario ?? 0;

            newInputs[id] = {
              ...newInputs[id],
              cantidad,
              costoUnitario,
              costoTotal: getQuoteItemTotal(itemLabel, cantidad, costoUnitario),
              isDiscount: isDiscountItemLabel(itemLabel),
              // observaciones: row.Observaciones || row.observaciones || "",
              refSubDataIdPedido: id,
            };
          }
        });

        setInputs(newInputs);
        toast.success("Items importados correctamente");
        // Reset file input
        e.target.value = "";
      } catch (error) {
        console.error(error);
        toast.error("Error al procesar el archivo Excel");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleInputChange = (reqId, fieldName, value) => {
    const requestItem = validateArray(requestItems)
      ? requestItems.find((item) => String(item.id) === String(reqId))
      : null;

    setInputs((prev) => {
      const row = prev[reqId] || {
        cantidad: 0,
        costoUnitario: 0,
        costoTotal: 0,
        observaciones: "",
      };
      const newRow = { ...row, [fieldName]: value };
      if (fieldName === "cantidad" || fieldName === "costoUnitario") {
        const itemLabel = requestItem?.label || newRow.labelItem || "";
        newRow.costoTotal = getQuoteItemTotal(
          itemLabel,
          newRow.cantidad,
          newRow.costoUnitario,
        );
        newRow.isDiscount = isDiscountItemLabel(itemLabel);
      }
      return { ...prev, [reqId]: newRow };
    });
  };

  async function handleSubmit() {
    if (!providerName) return toast.error("Ingrese el nombre del proveedor");

    const itemsArray = validateArray(requestItems)
      ? requestItems.map((reqItem) => {
          const row = inputs[reqItem.id] || {};
          const isDiscount = isDiscountItemLabel(reqItem.label);

          return {
            ...row,
            costoTotal: getQuoteItemTotal(
              reqItem.label,
              row.cantidad,
              row.costoUnitario,
            ),
            isDiscount,
            refSubDataIdPedido: reqItem.id,
            labelItem: reqItem.label,
          };
        })
      : [];

    const itemsTotal = itemsArray.reduce(
      (acc, curr) => acc + Number(curr.costoTotal || 0),
      0,
    );
    const logisticTotal = logisticCosts.reduce(
      (acc, curr) => acc + Number(curr.monto || 0),
      0,
    );

    const payload = {
      Label: providerName,
      Description2: JSON.stringify({
        label: providerName,
        items: itemsArray,
        paymentTerms,
        incoterms,
        deliveryDate,
        logisticCosts,
        itemsTotal,
        logisticTotal,
        currency: activeCurrency,
      }),
      Description3: existingQuote?.description3 || "false",
      Description4: existingQuote?.description4 || "[]",
      NumberValue: itemsTotal + logisticTotal,
      dataSetId: field.dataSetId,
    };

    try {
      await requestAuth(
        existingQuote ? "put" : "post",
        existingQuote
          ? `/dataSet/procedure/subData/${existingQuote.id}`
          : `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
        payload,
      );
      reload();
      onClose();
      toast.success("Cotización guardada");
    } catch (err) {
      toast.error("Error al guardar");
    }
  }

  return (
    <div className="flex flex-col gap-4 p-4 bg-white rounded-lg">
      <H3 className="border-b pb-2 text-gray-700">
        {existingQuote
          ? "Modificar Cotización"
          : "Nueva Cotización de Proveedor"}
      </H3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="flex flex-col gap-1">
          <Label className="text-gray-600">Nombre del Proveedor</Label>
          <Select
            className="text-sm"
            placeholder="Seleccione un proveedor..."
            options={
              validateArray(providers)
                ? providers.map((prov) => {
                    let code = "";
                    try {
                      const dataObj = prov.data ? JSON.parse(prov.data) : {};
                      code = dataObj.current_code || "";
                    } catch (e) {}
                    return {
                      value: prov.name,
                      label: prov.name + (code ? ` - ${code}` : ""),
                    };
                  })
                : []
            }
            value={
              providerName ? { label: providerName, value: providerName } : null
            }
            onChange={(selected) => setProviderName(selected?.value || "")}
            isClearable
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-gray-600">Términos de Pago</Label>
          <Select
            className="text-sm"
            placeholder="Términos de pago..."
            options={mapDropdownOptions(paymentTermsList)}
            value={
              paymentTerms
                ? { label: paymentTerms, value: paymentTerms }
                : null
            }
            onChange={(selected) => setPaymentTerms(selected?.value || "")}
            isClearable
            isLoading={!loadingPaymentTerms}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-gray-600">Incoterms</Label>
          <Select
            className="text-sm"
            placeholder="Incoterms..."
            options={mapDropdownOptions(incotermsList)}
            value={incoterms ? { label: incoterms, value: incoterms } : null}
            onChange={(selected) => setIncoterms(selected?.value || "")}
            isClearable
            isLoading={!loadingIncoterms}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-gray-600">Fecha de Entrega</Label>
          <input
            type="date"
            className="p-2 border rounded focus:ring-2 focus:ring-blue-200 outline-none transition-all"
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-gray-600">Moneda</Label>
          <div className="p-2 border rounded bg-gray-50 text-gray-800 font-semibold">
            {activeCurrency}
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mt-2">
        <H3 className="text-gray-700 text-sm font-bold">Items de la Cotización</H3>
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={handleExportItems}
            className="px-3 py-1 text-xs bg-gray-600 hover:bg-gray-700"
          >
            <i className="fas fa-file-export mr-1"></i> Exportar Items
          </Button>
          <div className="relative">
            <Button
              type="button"
              className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700"
            >
              <i className="fas fa-file-import mr-1"></i> Importar Items
            </Button>
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleImportItems}
              className="absolute inset-0 opacity-0 cursor-pointer"
            />
          </div>
        </div>
      </div>

      <div className="overflow-hidden border rounded-lg shadow-sm mb-4">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-100 text-gray-600 uppercase text-[11px]">
            <tr>
              <th className="px-3 py-3">Item</th>
              <th className="px-3 py-3 w-24">Cant.</th>
              <th className="px-3 py-3 w-32">Precio Unitario</th>
              <th className="px-3 py-3 w-32">Total</th>
              <th className="px-3 py-3">Obs.</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {validateArray(requestItems) &&
              requestItems.map((reqItem) => {
                const row = inputs[reqItem.id] || {};
                const meta = reqItem.description2
                  ? JSON.parse(reqItem.description2)
                  : {};
                const isDiscount = isDiscountItemLabel(reqItem.label);
                return (
                  <tr
                    key={reqItem.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-3 py-2">
                      <p className="font-medium text-gray-800">
                        {reqItem.label}
                        {isDiscount && (
                          <span className="ml-2 rounded bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase text-red-600">
                            Descuento
                          </span>
                        )}
                      </p>
                      <p className="text-[10px] text-gray-400 uppercase">
                        {meta.desc} {meta.tr4 ? `| TR4: ${meta.tr4}` : ""}{" "}
                        {meta.providerCode
                          ? `| Prov: ${meta.providerCode}`
                          : ""}
                      </p>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        className="w-full border rounded p-1"
                        value={row.cantidad || ""}
                        placeholder={meta.amount}
                        onChange={(e) =>
                          handleInputChange(
                            reqItem.id,
                            "cantidad",
                            e.target.value,
                          )
                        }
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="w-full border rounded p-1"
                        value={formatMoneyInput(row.costoUnitario)}
                        onChange={(e) =>
                          handleInputChange(
                            reqItem.id,
                            "costoUnitario",
                            cleanMoneyValue(e.target.value),
                          )
                        }
                      />
                    </td>
                    <td
                      className={`px-3 py-2 font-semibold ${
                        isDiscount ? "text-red-600" : "text-gray-600"
                      }`}
                    >
                      {formatCost(row.costoTotal)}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        className="w-full border rounded p-1"
                        value={row.observaciones || ""}
                        onChange={(e) =>
                          handleInputChange(
                            reqItem.id,
                            "observaciones",
                            e.target.value,
                          )
                        }
                      />
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-2 mt-4 mb-4">
        <div className="flex justify-between items-center">
          <H3 className="text-gray-700 text-sm font-bold">Costos Logísticos</H3>
          <Button
            onClick={handleAddLogisticCost}
            type="button"
            className="px-3 py-1 text-xs"
          >
            + Añadir
          </Button>
        </div>
        {logisticCosts.length > 0 && (
          <div className="border rounded-lg shadow-sm overflow-hidden">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-100 text-gray-600 uppercase text-[11px]">
                <tr>
                  <th className="px-3 py-2 w-[45%]">Costo</th>
                  <th className="px-3 py-2 w-[45%]">Monto</th>
                  <th className="px-3 py-2 w-[10%]"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {logisticCosts.map((cost, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-3 py-2">
                      <Select
                        className="text-xs"
                        options={(logisticCostsList || []).map((opt) => ({
                          label: opt.name || opt.id || opt.nombre,
                          value: opt.name || opt.id || opt.nombre,
                        }))}
                        value={
                          cost.type
                            ? { label: cost.type, value: cost.type }
                            : null
                        }
                        onChange={(selected) =>
                          handleLogisticCostChange(
                            index,
                            "type",
                            selected?.value || "",
                          )
                        }
                        placeholder="Seleccione..."
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="w-full border rounded p-1 text-xs"
                        value={formatMoneyInput(cost.monto)}
                        onChange={(e) =>
                          handleLogisticCostChange(
                            index,
                            "monto",
                            cleanMoneyValue(e.target.value),
                          )
                        }
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => handleRemoveLogisticCost(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex justify-between mt-2 border-t pt-4">
        <div className="font-bold text-gray-700 text-sm flex flex-col gap-1">
          <p>
            Total Factura:{" "}
            {formatCost(
              Object.values(inputs).reduce(
                (acc, curr) => acc + (curr.costoTotal || 0),
                0,
              ),
            )}
          </p>
          <p>
            Costos Logísticos:{" "}
            {formatCost(
              logisticCosts.reduce(
                (acc, curr) => acc + Number(curr.monto || 0),
                0,
              ),
            )}
          </p>
          <p className="border-t pt-1 text-base text-blue-800">
            Precio Total:{" "}
            {formatCost(
              Object.values(inputs).reduce(
                (acc, curr) => acc + (curr.costoTotal || 0),
                0,
              ) +
              logisticCosts.reduce(
                (acc, curr) => acc + Number(curr.monto || 0),
                0,
              ),
            )}
          </p>
        </div>
        <div className="flex items-end gap-3 pt-2">
          <button
            type="button"
            className="px-4 py-2 text-gray-500 hover:text-gray-700 font-medium"
            onClick={onClose}
          >
            Cancelar
          </button>
          <Button onClick={handleSubmit} className="shadow-md">
            Confirmar Registro
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InputSubDataProviderAdd;
